import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import { ProgramStatus } from '../enums/ProgramStatus';
import { getJsDateFromString } from './DateUtils';

export interface CategoryReward {
  id: string,
  name: string,
  points: number,
  totalSpend: number,
}

export const hasAutoFirstTier = (
  tiers: { minimumDollars: number, minimumSegments: number, rank: number }[],
): boolean => tiers.some(
  (tier) => tier.rank === 0
    && tier.minimumSegments === 0
    && tier.minimumDollars === 0,
);

export const getCategoriesToNextTier = (
  farmerRewards: { segmentsHurdled: number, segmentsRemaining: number } | null | undefined,
  tiers: { minimumDollars: number, minimumSegments: number, rank: number }[],
) => {
  let numSegmentsToHurdle;
  if (farmerRewards) {
    numSegmentsToHurdle = farmerRewards.segmentsRemaining;
  } else {
    const nextRank = hasAutoFirstTier(tiers) ? 1 : 0;
    numSegmentsToHurdle = tiers.find((tier) => tier.rank === nextRank)?.minimumSegments ?? 0;
  }
  return numSegmentsToHurdle;
};

export const getProgramStatus = (
  program: { startDate?: string, endDate?: string },
): ProgramStatus | null => {
  if (!program.startDate || !program.endDate) {
    return null;
  }
  const currentTime = Date.now();

  const programStartTime = getJsDateFromString(program.startDate,
    { timezone: 'PT' },
  ).getTime();

  const programEndTime = getJsDateFromString(program.endDate,
    { endOfDay: true, timezone: 'PT' },
  ).getTime();

  if (currentTime < programStartTime) {
    return ProgramStatus.Future;
  }
  if (currentTime > programEndTime) {
    return ProgramStatus.Expired;
  }
  return ProgramStatus.Active;
};

export const getTierName = (
  tiers: { minimumDollars: number, minimumSegments: number, name: string, rank: number }[],
  rank: number | null | undefined,
  noneString?: string,
): string => {
  if (isNil(rank) && hasAutoFirstTier(tiers)) {
    rank = 0;
  }
  const noneLabel = noneString ?? 'None';
  return tiers.find((tier) => tier.rank === rank)?.name ?? noneLabel;
};

export const getTierOverrideDates = (
  currentOrFutureRewardsProgram: { endDate: string, startDate: string },
) => {
  const start = DateTime.fromISO(currentOrFutureRewardsProgram.startDate);
  const overrideStartDate = start.minus({ years: 1 }).toISODate()!;
  const overrideEndDate = start.minus({ days: 1 }).toISODate()!;

  return { overrideEndDate, overrideStartDate };
};

export const shouldHideZeroDollarTotalSpendForCategory = (categoryReward: CategoryReward) => {
  if (categoryReward.totalSpend !== 0) {
    return false;
  }
  const ZeroTotalSpendToHideForCategories = [
    'soybean',
    'corn',
    'grain',
  ];
  const regexPattern = new RegExp(ZeroTotalSpendToHideForCategories.join('|'), 'i');
  return regexPattern.test(categoryReward.name);
};

/**
 * Returns program duration in days, with an inclusive end date.
 * e.g. 01/01/2024 -> 12/31/2024 returns 365 for a 365-day program duration
 */
export const getDurationInDays = (dateString1: string, dateString2: string) => {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;

  const d1 = new Date(dateString1);
  const d2 = new Date(dateString2);

  const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());

  return Math.round(Math.abs(utc2 - utc1) / MS_PER_DAY) + 1;
};
